import { RouterRootState } from 'connected-react-router';
import { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { toast } from "react-toastify";
import ActivityFrame from '../../../components/activity-frame/activity-frame';
import Description from '../../../components/description/Description';
import { useDialog } from '../../../components/dialog/DialogContext';
import ItemContentTabTwo from '../../../components/item-content-two/item-content-tab_two';
import { Topic } from '../../../components/item-content-two/Topic';
import SimpleItemDetails from '../../../components/item-content/SimpleItemDetails';
import { ControlIdPresets } from '../../../components/preset/ControlIdPresets';
import { HikVisionPresets } from '../../../components/preset/HikVisionPresets';
import { IdemiaPresets } from '../../../components/preset/IdemiaPresets';
import PresetActions from '../../../components/preset/presetActions';
import PresetBiometric from "../../../components/preset/presetBiometric";
import { PresetCard } from '../../../components/preset/PresetCard';
import PresetsDeviceSchedules from '../../../components/preset/presetDeviceSchedules';
import PresetInputs from '../../../components/preset/presetInputs';
import PresetOutputs from '../../../components/preset/presetOutputs';
import PresetParameters from '../../../components/preset/presetParameters';
import PresetReaders from '../../../components/preset/presetReaders';
import { SystemProfileTypeIds } from '../../../components/preset/presetSettingsWizard';
import { SelectType } from '../../../components/settingFields/SettingSelectField';
import { CONTROL_ID_SETTINGS, HIK_VISION_SETTINGS, IDEMIA_SETTINGS, SUB_TYPES } from '../../../constants/preset-constants';
import { SPECIFIC_HELP_KEY } from '../../../constants/specific-help-constant';
import { useLocale } from '../../../contexts/localeContext';
import { TopicContextProvider } from '../../../contexts/topic/TopicContextProvider';
import { TranslationKeys } from '../../../contexts/TranslationKeys';
import { BreadcrumbLink } from '../../../models/BreadcrumbLink';
import { ContentWrapper } from '../../../models/ContentWrapper';
import { DevicePreset as BiometricDevicePreset, DevicePreset } from '../../../models/DevicePreset';
import { Lookup } from "../../../models/Lookup";
import LookupTypes from "../../../models/LookupTypes";
import { MultiSelectOption } from '../../../models/MultiSelectOption';
import { default as BiometricSearch } from "../../../models/search/BiometricSearch";
import { DeleteEntityResult } from '../../../models/services/DeleteEntityResult';
import { TabSummary } from '../../../models/TabSummary';
import { useGlobalState } from '../../../Providers';
import { getPresetInputsByProfileId } from '../../../services/biometricPresetService';
import { getBiometricDevicePresetNameWithPagination, getBiometricPresetById, storeBioPreset } from '../../../services/hardwarePresetService';
import { deletePresetSetting, updateBioPreset } from '../../../services/presetService';
import { getSiteNamesWithPagination } from "../../../services/siteService";
import { getErrorMessage, showLoadItemError } from '../../../services/utilities/ServiceError';
import { IAppState } from '../../../store/types';

const getIdFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const allItemsPath = '/access-control/hardware/presets';
const itemPath = '/access-control/hardware/biometric-preset/';

interface Content {
    tabContent?: ContentWrapper[],
    tabs?: any[]
}

const initialItem: BiometricDevicePreset = {
    id: 0,
    name: "",
    type: "",
    devices: 0,
    site: "",
    profileTypeId: "",
    pInheritsFrom: "",
    inheritsFromId: 0,
    siteId: "",
    description: "",
    totalActions: 0,
    totalParameters: 0,
    totalInputs: 0,
    totalDevices: 0,
    totalModules: 0,
    totalDoorModePatterns: 0,
    totalOutputs: 0,
    profileSubTypeId: "",
    pSysnameType: "",
    pAlias: "",
}

const BiometricPreset: FC = () => {
    const { translate } = useLocale();
    const initialTabs = [
        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true },
    ];
    const tabContentList: ContentWrapper[] = [
        {
            content: <Description title={translate.byKey(TranslationKeys.notes)} />
        }];
    const siteId = useSelector((state: IAppState) => state.site.site.value);
    const history = useHistory();
    const pageId = useSelector((state: RouterRootState) => getIdFromPath(state.router.location.pathname));
    const [itemId, setItemId] = useState(pageId);
    const [currentItem, setCurrentItem] = useState<BiometricDevicePreset>(initialItem);
    const [breadcrumb, setBreadcrumb] = useState<BreadcrumbLink[]>([]);
    const [busy, setBusy] = useState(false);
    const [tabs, setTabs] = useState<TabSummary[]>([]);
    const [content, setContent] = useState<any>(tabContentList);
    const dialog = useDialog();
    const [selectedItem, setSelectedItem] = useState<MultiSelectOption | undefined>(undefined);
    const {setSpecificHelpTopicKey} = useGlobalState() ?? {setSpecificHelpTopicKey: undefined};

    const topic: Topic = {
        accessor: 'nameOriginal',
        title: translate.byKey(TranslationKeys.biometricPresets),
        allItemsPath: allItemsPath,
        itemPath: itemPath,
    }

    const lookup: Lookup[] = [ 
        {
            name: LookupTypes.site,
            values: [],
            dataMethod: getSiteNamesWithPagination,
            searchAccessor: BiometricSearch.find(item => item.accessor === "site")?.searchOnName,
        }
    ]

    const reloadPage = async () => {
        try {
            setBusy(true);
            let biometricPreset;
            let content;

            setTabs([]);
            setCurrentItem(initialItem);

            if (itemId) {
                biometricPreset = await getBiometricPresetById(itemId);
                const settingData = await getPresetInputsByProfileId(itemId, translate);
                const currentItem = settingData.customData;

                // TODO: Remove this when backend is fixed
                if (biometricPreset.profileSubTypeId == SUB_TYPES.IDEMIA  // Only applies to IDEMIA preset.
                    && currentItem?.BIO_WIEGAND_SITE_FORMAT.value === '0') {
                    currentItem.BIO_WIEGAND_SITE_FORMAT.value = '0.0'
                }

                setSelectedItem({ label: biometricPreset.name, value: biometricPreset.id.toString(), isDefault: biometricPreset.isDefault })
                setCurrentItem({ ...biometricPreset, custom: currentItem });
                content = getReportContent(biometricPreset, settingData, biometricPreset);
            }

            setTabs(content?.tabs ? content.tabs : initialTabs);
            setContent(content?.tabContent ? content.tabContent : tabContentList);

            setBreadcrumb([
                { value: '/access-control/hardware', displayName: translate.byKey(TranslationKeys.hardware) },
                { value: allItemsPath, displayName: translate.byKey(TranslationKeys.preset) },
                { value: itemPath + `${itemId}`, displayName: `${biometricPreset?.name ?? ''}` }
            ]);
        } catch (error) {
            showLoadItemError(error, dialog, translate, () => { history.push(allItemsPath) });
        } finally {
            setBusy(false);
        }
    }

    const getReportContent = (biometricPreset: BiometricDevicePreset, settingData: any, currentItem): Content => {
        // Clear any previously set key so preset types without specific help topics never
        // show stale topics left over from the last visited preset.
        setSpecificHelpTopicKey && setSpecificHelpTopicKey(undefined);
        switch (biometricPreset.profileSubTypeId) {
            case SUB_TYPES.IDEMIA:
                setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.IDEMIA_PRESET);
                return {
                    tabContent: [
                        {
                            content: <IdemiaPresets presetId={itemId} idemiaPresetCardType={IDEMIA_SETTINGS.COMMS} />
                        },
                        {
                            content: <IdemiaPresets presetId={itemId} idemiaPresetCardType={IDEMIA_SETTINGS.WIEGAND} />
                        },
                        {
                            content: <IdemiaPresets presetId={itemId} idemiaPresetCardType={IDEMIA_SETTINGS.THREAT_LEVEL} />
                        },
                        {
                            content: <IdemiaPresets presetId={itemId} idemiaPresetCardType={IDEMIA_SETTINGS.TIME_AND_ATTENDANCE} />
                        },
                        {
                            content: <PresetActions itemId={itemId} onDelete={(id: any) => onDelete(id, 4)} onSave={(createNew) => updateBadge(0, createNew)} profileId={SystemProfileTypeIds.idemia} currentItemDetails={currentItem} reportTitle={translate.byKey(TranslationKeys.messaging)} />
                        },
                        {
                            content: <IdemiaPresets presetId={itemId} idemiaPresetCardType={IDEMIA_SETTINGS.ADVANCED} />
                        },
                        {
                            content: <PresetBiometric itemId={itemId} />
                        },
                        {
                            content: <Description title={translate.byKey(TranslationKeys.notes)} />
                        },
                    ],
                    tabs: [
                        { label: translate.byKey(TranslationKeys.communications), badge: 0, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.wiegand), badge: 0, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.threatLevel), badge: 0, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.timeAndAttendance), badge: 0, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.messaging), badge: 0, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.advanced), badge: 0, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.biometrics), badge: currentItem.totalDevices, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true },
                    ],

                }
            case SUB_TYPES.CONTROL_ID:
                setSpecificHelpTopicKey && setSpecificHelpTopicKey(SPECIFIC_HELP_KEY.CONTROL_ID_PRESET);
                return {
                    tabContent: [
                        {
                            content: <ControlIdPresets presetId={itemId} presetCardType={CONTROL_ID_SETTINGS.IDENTIFICATION} />
                        },
                        {
                            content: <ControlIdPresets presetId={itemId} presetCardType={CONTROL_ID_SETTINGS.FACE_SETTINGS} />
                        },
                        {
                            content: <ControlIdPresets presetId={itemId} presetCardType={CONTROL_ID_SETTINGS.WIEGAND} />
                        },
                        {
                            content: <ControlIdPresets presetId={itemId} presetCardType={CONTROL_ID_SETTINGS.TIME_AND_ATTENDANCE} />
                        },
                        {
                            content: <ControlIdPresets presetId={itemId} presetCardType={CONTROL_ID_SETTINGS.OSDP} />
                        },
                        {
                            content: <PresetBiometric itemId={itemId} />
                        },
                        {
                            content: <Description title={translate.byKey(TranslationKeys.notes)} />
                        },
                    ],
                    tabs: [
                        { label: translate.byKey(TranslationKeys.details), badge: 0, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.face), badge: 0, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.wiegand), badge: 0, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.timeAndAttendance), badge: 0, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.osdp), badge: 0, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.biometrics), badge: currentItem.totalDevices, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true },
                    ],

                }
            case SUB_TYPES.HID_AMICO:
                return {
                    tabContent: [
                        {
                            content: <ControlIdPresets presetId={itemId} presetCardType={CONTROL_ID_SETTINGS.IDENTIFICATION} />
                        },
                        {
                            content: <ControlIdPresets presetId={itemId} presetCardType={CONTROL_ID_SETTINGS.FACE_SETTINGS} />
                        },
                        {
                            content: <ControlIdPresets presetId={itemId} presetCardType={CONTROL_ID_SETTINGS.WIEGAND} />
                        },
                        {
                            content: <ControlIdPresets presetId={itemId} presetCardType={CONTROL_ID_SETTINGS.TIME_AND_ATTENDANCE} />
                        },
                        {
                            content: <ControlIdPresets presetId={itemId} presetCardType={CONTROL_ID_SETTINGS.OSDP} />
                        },
                        {
                            content: <PresetBiometric itemId={itemId} />
                        },
                        {
                            content: <Description title={translate.byKey(TranslationKeys.notes)} />
                        },
                    ],
                    tabs: [
                        { label: translate.byKey(TranslationKeys.details), badge: 0, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.face), badge: 0, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.wiegand), badge: 0, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.timeAndAttendance), badge: 0, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.osdp), badge: 0, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.biometrics), badge: currentItem.totalDevices, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true },
                    ],

                }
            case SUB_TYPES.HIK_VISION:
                return {
                    tabContent: [
                        {
                            content: <HikVisionPresets presetId={itemId} presetCardType={HIK_VISION_SETTINGS.COMMS} />
                        },
                        {
                            content: <HikVisionPresets presetId={itemId} presetCardType={HIK_VISION_SETTINGS.WIEGAND} />
                        },
                        {
                            content: <HikVisionPresets presetId={itemId} presetCardType={HIK_VISION_SETTINGS.AUTHENTICATION_SETTINGS} />
                        },
                        {
                            content: <HikVisionPresets presetId={itemId} presetCardType={HIK_VISION_SETTINGS.THERMAL_SETTINGS} />
                        },
                        {
                            content: <PresetBiometric itemId={itemId} />
                        },
                        {
                            content: <Description title={translate.byKey(TranslationKeys.notes)} />
                        },
                    ],
                    tabs: [
                        { label: translate.byKey(TranslationKeys.communications), badge: 0, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.wiegand), badge: 0, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.authentication), badge: 0, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.thermal), badge: 0, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.biometrics), badge: currentItem.totalDevices, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true },
                    ],

                }
            case SUB_TYPES.IMPRO_BIO:
            case SUB_TYPES.ASSETS:
                return {
                    tabContent: [
                        {
                            content: <PresetInputs onDelete={(id) => onDelete(id, 0)} currentItemDetails={currentItem} onSave={(createNew) => { updateBadge(0, createNew) }} profileId={SystemProfileTypeIds.Reader} itemId={itemId} />,
                        },
                        {
                            content: <PresetOutputs itemId={itemId} />,
                        },
                        {
                            content: <PresetActions onDelete={(id) => onDelete(id, 2)} currentItemDetails={currentItem} onSave={(createNew) => { updateBadge(0, createNew) }} profileId={SystemProfileTypeIds.Reader} itemId={itemId} />,
                        },
                        {
                            content: <PresetParameters onSave={(createNew) => { updateBadge(0, createNew) }} profileId={SystemProfileTypeIds.Reader} itemId={itemId} />,
                        },
                        {
                            content: <PresetsDeviceSchedules itemId={itemId} onDelete={(id) => onDelete(id, 4)} onSave={(createNew) => { updateBadge(0, createNew) }} profileType={SystemProfileTypeIds.Reader} onDeleteUpdateBadge={(isDeleted) => updateBadge(4, isDeleted)} />,
                        },
                        {
                            content: <PresetReaders itemId={itemId} />
                        },
                        {
                            content: <Description title={translate.byKey(TranslationKeys.notes)} />
                        }
                    ],
                    tabs: [
                        { label: translate.byKey(TranslationKeys.inputs), badge: currentItem.totalInputs, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.outputs), badge: currentItem.totalOutputs, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.actions), badge: currentItem.totalActions, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.parameters), badge: currentItem.totalParameters, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.schedules), badge: currentItem.totalDoorModePatterns, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.biometrics), badge: currentItem.totalDevices, showInitial: true, isActive: true },
                        { label: translate.byKey(TranslationKeys.notes), badge: 0, showInitial: true, isActive: true }
                    ]
                }

            default:
                return {};
        }
    }

    const onDelete = (id: string, index: number): Promise<DeleteEntityResult> => {
        setBusy(true);
        return new Promise((resolve, reject) => {
            deletePresetSetting(id)
                .then(
                    deleteResult => {
                        resolve(deleteResult);
                        updateBadge(index, true);
                        setBusy(false);
                    },
                    deleteResult => {
                        reject(deleteResult);
                        setBusy(false);
                    })
        });
    }

    const updateBadge = (index, updateBadge: boolean) => {
        if (updateBadge && tabs.length > 0) {
            setTabs(updateTabBadgeTotal(index, tabs[index].badge - 1, tabs));
        }
    }

    const updateTabBadgeTotal = (tabIndex, totalCount, currentTabs) => {
        const newTabs = [...currentTabs];
        newTabs[tabIndex].badge = totalCount;
        newTabs[tabIndex].isActive = true; // Set tab to active so we can show it
        return newTabs;
    };

    useEffect(() => {
        setItemId(pageId);
    }, [pageId])

    useEffect(() => {
        reloadPage();
    }, [itemId])

    const getTabContent = (index?: number) => {
        if (content[index ?? 0]) {
            const selectedTabContent = content[index ?? 0];
            return selectedTabContent.content;
        }
        return content[0].content;
    };

    const isCopyingControlIdOrIdemia = (inheritsFromId: string) => {
        if (inheritsFromId === "60" || inheritsFromId === "69" || inheritsFromId === "85") {
            return true;
        }

        return false;
    }

    const saveBiometricPreset = (values) => {
        const newBioPreset: DevicePreset = {
            ...currentItem,
            name: values.name,
            id: values.id,
            siteId: isCopyingControlIdOrIdemia(values.inheritsFromId) ? 1 : currentItem?.siteId ? currentItem?.siteId : siteId,
            description: values.description,
            profileSubTypeId: values.profileSubTypeId,
            pInheritsFrom: values.pInheritsFrom,
            inheritsFromId: values.inheritsFromId,
            totalDevices: values.totalBiometrics,
            pSysnameType: values.pSysnameType,
            pAlias: values.pAlias,
            profileTypeId: values.profileTypeId,
            type: values.type
        }
        const isNew = newBioPreset.id ? false : true;
        
        setBusy(true);
        updateBioPreset(values)
            .then(() => storeBioPreset(newBioPreset, isNew))
            .then(async (value: any) => {
                toast.success(translate.byKey(TranslationKeys.biometricPresetSaved));

                setItemId(value.id)
                history.push(`${itemPath}${value.id}`);
                setCurrentItem(newBioPreset);
            })
            .catch(failure => {
                dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(failure) });
            })
            .finally(() => {
                setBusy(false);
            });
    }

    return <ActivityFrame busy={busy} className='flex w-full h-full'>
        <TopicContextProvider dataMethod={getBiometricDevicePresetNameWithPagination} itemId={itemId} topic={topic} selectedItem={selectedItem}>
            <ItemContentTabTwo>
                <SimpleItemDetails
                    breadcrumb={breadcrumb}
                    busy={busy}
                    getTabContent={getTabContent}
                    itemProfile={currentItem}
                    onBack={() => history.push(allItemsPath)}
                    saveDetails={saveBiometricPreset}
                    tabs={tabs}
                    initialValues={currentItem}
                    itemPath={itemPath}>
                    <PresetCard presetType={SelectType.bioMetricProfile}></PresetCard>
                </SimpleItemDetails>
            </ItemContentTabTwo>
        </TopicContextProvider>
    </ActivityFrame>
}

export default BiometricPreset;
